/**
 * Utility functions for the PrettyJSONEditor component
 */
import { RefObject } from 'react';

/**
 * Focuses an element and sets the cursor to the end of its content
 */
export const focusElementAndSetCursorToEnd = (
  element: HTMLElement | null
): void => {
  if (!element) return;

  element.focus();

  // Set cursor to the end if the element supports it
  if ('setSelectionRange' in element) {
    const length = (element as HTMLInputElement | HTMLTextAreaElement).value
      .length;
    (element as HTMLInputElement | HTMLTextAreaElement).setSelectionRange(
      length,
      length
    );
  }
};

/**
 * Finds the last value input in the editor
 */
export const findLastValueInput = (
  valueInputRefs: Record<
    string,
    RefObject<HTMLInputElement | HTMLTextAreaElement>
  >
): HTMLElement | null => {
  const lastKey = Object.keys(valueInputRefs).pop();
  if (!lastKey) return null;
  return valueInputRefs[lastKey].current as HTMLElement;
};

/**
 * Finds a value input by index
 */
export const findValueInputByIndex = (
  index: number,
  valueInputRefs: Record<
    string,
    RefObject<HTMLInputElement | HTMLTextAreaElement>
  >
): HTMLElement | null => {
  const key = Object.keys(valueInputRefs)[index];
  if (!key) return null;
  return valueInputRefs[key].current as HTMLElement;
};

/**
 * Validates if a key is empty
 */
export const isKeyValid = (key: string): boolean => {
  return key.trim().length > 0;
};

/**
 * Checks if a key already exists in the key order
 */
export const isDuplicateKey = (
  key: string,
  keyOrder: string[],
  currentKey?: string
): boolean => {
  // If we're updating an existing key, it's not a duplicate if it's the same key
  if (currentKey && key === currentKey) return false;

  return keyOrder.includes(key);
};

/**
 * Creates a duplicate key error message
 */
export const createDuplicateKeyErrorMessage = (key: string): string => {
  return `Key "${key}" already used`;
};

/**
 * Checks if the cursor is at the beginning of an input
 */
export const isCursorAtBeginning = (
  target: HTMLInputElement | HTMLTextAreaElement
): boolean => {
  return target.selectionStart === 0 && target.selectionEnd === 0;
};

/**
 * Checks if the cursor is at the end of an input
 */
export const isCursorAtEnd = (
  target: HTMLInputElement | HTMLTextAreaElement,
  value: string
): boolean => {
  return target.selectionStart === value.length;
};

/**
 * Schedules a focus operation with a small delay
 */
export const scheduleFocus = (
  element: HTMLElement | null,
  setToEnd: boolean = false
): void => {
  setTimeout(() => {
    if (!element) return;

    element.focus();

    if (setToEnd && 'setSelectionRange' in element) {
      const length = (element as HTMLInputElement | HTMLTextAreaElement).value
        .length;
      (element as HTMLInputElement | HTMLTextAreaElement).setSelectionRange(
        length,
        length
      );
    }
  }, 0);
};

/**
 * Validates if a JSON object has nested objects
 */
export const hasNestedObjects = (obj: Record<string, any>): boolean => {
  for (const key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      return true;
    }
  }
  return false;
};

/**
 * Validates the JSON - if it has nested objects, return an error message
 */
export const validateJSON = (json: Record<string, any>): string | null => {
  if (hasNestedObjects(json)) {
    return 'Nested JSON detected';
  }
  return null;
};

/**
 * Calculates the width of text to properly size the input element
 */
export const calculateTextWidth = (
  text: string,
  element: HTMLElement
): number => {
  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.position = 'absolute';
  span.style.fontSize = window.getComputedStyle(element).fontSize;
  span.style.fontFamily = window.getComputedStyle(element).fontFamily;
  span.style.fontWeight = window.getComputedStyle(element).fontWeight;
  span.innerText = text;
  // Replace newlines with spaces to prevent them from affecting width calculation
  span.innerText = span.innerText.replace(/\n/g, ' ');
  document.body.appendChild(span);

  const width = span.offsetWidth + 4; // Add small buffer
  document.body.removeChild(span);

  return width;
};

/**
 * Adjusts the height of the textarea to fit its content
 */
export const adjustTextareaHeight = (textarea: HTMLTextAreaElement): void => {
  if (!textarea) return;

  // Store the current scroll position
  const scrollTop = window.scrollY;

  // Reset height to allow proper calculation
  textarea.style.height = '0px';

  // Set to scrollHeight to get the exact content height
  const scrollHeight = textarea.scrollHeight;
  textarea.style.height = `${scrollHeight}px`;

  // Restore scroll position to prevent page jump
  window.scrollTo(0, scrollTop);
};

export const INPUT_KEY_CLASS_NAME = 'first-element-input-key';
export const INPUT_VALUE_CLASS_NAME = 'first-element-input-value';

export const focusOnFirstValueInput = (element?: Element): void => {
  requestAnimationFrame(() => {
    let firstInput = element?.querySelector(
      `textarea.${INPUT_VALUE_CLASS_NAME}`
    ) as HTMLTextAreaElement;

    if (!firstInput) {
      firstInput = document.querySelector(
        `textarea.${INPUT_VALUE_CLASS_NAME}`
      ) as HTMLTextAreaElement;
    }

    if (firstInput) {
      firstInput.focus();
      if (firstInput.value) {
        // If there is text in the input, move cursor to the end
        const len = firstInput.value.length;
        firstInput.setSelectionRange(len, len);
      }
    }
  });
};
